import { resetAlert } from "../../helpers";
import type { AlertType } from "../../helpers";
import {
  createYards,
  updateYard,
} from "../../../../store/oasis/yardsStore";

export interface Yard {
  resellerId: string;
  name: string;
  location: {
    address: string;
    centerLatitude: number;
    centerLongitude: number;
    entryLatitude: number;
    entryLongitude: number;
    mapLink: string;
    radius: number;
  };
  phone: {
    areaCode: string;
    areaCodeNumber: string;
    number: string;
  };
  timeZone: string;
  createdAt: {
    _seconds: number;
    _nanoseconds: number;
  };
  updatedAt: {
    _seconds: number;
    _nanoseconds: number;
  };
  id?: string;
}

interface TimeZone {
  value: string;
  name: string;
}

interface Option {
  value: string;
  label: string;
}

type SetAlert = (options: AlertType) => void;
type Reset = () => void;
type Navigate = (path: string) => void;
type Translate = (key: string) => string;

const timeZones = [
  { value: "America/Argentina/Buenos_Aires", name: "Argentina - Buenos Aires" },
  { value: "America/Argentina/Cordoba", name: "Argentina - Cordoba" },
  { value: "America/Sao_Paulo", name: "Brasil - Sao Paulo" },
  { value: "America/Bogota", name: "Colombia - Bogota" },
  { value: "America/Bahia_Banderas", name: "Mexico - Bahia Banderas" },
  { value: "America/Cancun", name: "Mexico - Cancun" },
  { value: "America/Chihuahua", name: "Mexico - Chihuahua" },
  { value: "America/Ciudad_Juarez", name: "Mexico - Ciudad Juarez" },
  { value: "America/Hermosillo", name: "Mexico - Hermosillo" },
  { value: "America/Matamoros", name: "Mexico - Matamoros" },
  { value: "America/Mazatlan", name: "Mexico - Mazatlan" },
  { value: "America/Merida", name: "Mexico - Merida" },
  { value: "America/Mexico_City", name: "Mexico - Mexico City" },
  { value: "America/Monterrey", name: "Mexico - Monterrey" },
  { value: "America/Ojinaga", name: "Mexico - Ojinaga" },
  { value: "America/Phoenix", name: "Mexico - Phoenix" },
  { value: "America/Tijuana", name: "Mexico - Tijuana" },
  { value: "America/Caracas", name: "Venezuela - Caracas" },
];

export const options = (t: (key: string) => string): Option[] => [
  {
    value: "",
    label: t("selectTimeZone"),
  },
  ...(timeZones?.map(
    (timeZone: TimeZone): Option => ({
      value: timeZone.value,
      label: timeZone.name,
    })
  ) || []),
];

export const onCreate = async (
  data: any,
  setAlert: SetAlert,
  reset: Reset,
  navigate: Navigate,
  t: Translate
): Promise<void> => {
  try {
    await createYards(data);

    return setAlert({
      visible: true,
      title: t("successful"),
      text: t("truckYardCreated"),
      icon: "success",
      confirmButtonText: t("closeButton"),
      confirmButtonColor: "#1C93FF",
      onConfirm: () => {
        setAlert(resetAlert());
        reset();
        setTimeout(() => {
          navigate("/oasis/yards");
        }, 150);
      },
      showCancelButton: false,
    });
  } catch (error) {
    if (
      typeof error === "object" &&
      error !== null &&
      "message" in error &&
      typeof (error as { message?: unknown }).message === "string" &&
      (error as { message: string }).message === "Server Error"
    ) {
      return setAlert({
        visible: true,
        title: "500",
        text: t("internalServerError"),
        icon: "error",
        confirmButtonText: t("closeButton"),
        confirmButtonColor: "#1C93FF",
        onConfirm: () => setAlert(resetAlert()),
        showCancelButton: false,
      });
    }
    return setAlert({
      visible: true,
      title: t("unexpectedError"),
      text: t("somethingWentWrong"),
      icon: "error",
      confirmButtonText: t("closeButton"),
      confirmButtonColor: "#1C93FF",
      onConfirm: () => setAlert(resetAlert()),
      showCancelButton: false,
    });
  }
};

export const onUpdate = async (
  data: any,
  setAlert: SetAlert,
  reset: Reset,
  navigate: Navigate,
  isEdit: boolean,
  setIsEdit: (value: boolean) => void,
  t: Translate,
  setLoading: (value: boolean) => void
): Promise<void> => {
  try {
    setLoading(true);
    await updateYard(data);
    return setAlert({
      visible: true,
      title: t("successful"),
      text: t("truckYardUpdated"),
      icon: "success",
      confirmButtonText: t("closeButton"),
      confirmButtonColor: "#1C93FF",
      onConfirm: () => {
        setAlert(resetAlert());
        reset();
        if (isEdit) {
          navigate("/oasis/yards");
          setIsEdit(false);
          localStorage.removeItem("isEditYard");
          localStorage.removeItem("yardSelected");
        }
      },
      showCancelButton: false,
    });
  } catch (error) {
    if (
      typeof error === "object" &&
      error !== null &&
      "message" in error &&
      typeof (error as { message?: unknown }).message === "string" &&
      (error as { message: string }).message === "Server Error"
    ) {
      return setAlert({
        visible: true,
        title: "500",
        text: t("internalServerError"),
        icon: "error",
        confirmButtonText: t("closeButton"),
        confirmButtonColor: "#1C93FF",
        onConfirm: () => setAlert(resetAlert()),
        showCancelButton: false,
      });
    }
    return setAlert({
      visible: true,
      title: t("unexpectedError"),
      text: t("somethingWentWrong"),
      icon: "error",
      confirmButtonText: t("closeButton"),
      confirmButtonColor: "#1C93FF",
      onConfirm: () => setAlert(resetAlert()),
      showCancelButton: false,
    });
  } finally {
    setLoading(false);
  }
};

// Verifica si todos los campos requeridos están llenos (para crear)
interface FormValues {
  name?: string;
  phone?: string;
  timeZone?: string;
  location?: {
    address: string;
    [key: string]: any;
  };
  [key: string]: any;
}

export const isFormValid = (values: FormValues): boolean => {
  return (
    typeof values.name === "string" &&
    values.name.trim() !== "" &&
    typeof values.phone === "string" &&
    values.phone.trim() !== "" &&
    typeof values.timeZone === "string" &&
    values.timeZone.trim() !== "" &&
    typeof values.location === "object" &&
    typeof values.location?.address === "string" &&
    values.location.address.trim() !== ""
  );
};

// Verifica si algún campo fue modificado respecto al original (para editar)
interface YardSelected {
  name: string;
  phone?: {
    number: string;
    [key: string]: any;
  };
  timeZone: string;
  location?: {
    address: string;
    [key: string]: any;
  };
  [key: string]: any;
}

interface FormChangedValues {
  name: string;
  phone: string;
  timeZone: string;
  location?: {
    address: string;
    [key: string]: any;
  };
  [key: string]: any;
}

export const isFormChanged = (
  isEdit: boolean,
  yardSelected: YardSelected | null | undefined,
  values: FormChangedValues
): boolean => {
  if (!isEdit || !yardSelected) return false;
  return (
    values.name !== yardSelected.name ||
    values.phone !== yardSelected.phone?.number ||
    values.timeZone !== yardSelected.timeZone ||
    values.location?.address !== yardSelected.location?.address
  );
};
